import {
  S3Client,
  PutObjectCommand,
  HeadObjectCommand,
  GetObjectCommand,
  DeleteObjectCommand,
} from '@aws-sdk/client-s3';

const AWS_ACCESS_KEY   = import.meta.env.VITE_WASABI_ACCESS_KEY;
const AWS_SECRET_KEY   = import.meta.env.VITE_WASABI_SECRET_KEY;
const BUCKET_NAME      = 'allwebsitebackupandabiggb';
const FOLDER_VIDEOS    = 'ShipAssure/Videos/';
const FOLDER_FILES     = 'ShipAssure/Files/';
const ENDPOINT         = 'https://s3.ap-southeast-1.wasabisys.com';
const VIDEO_URL_PREFIX = 'https://videos.shipassure.in/?v=';

// Newer AWS SDK versions add CRC32 checksum headers by default, which Wasabi
// rejects. Only calculate/validate checksums when an operation requires them.
const s3Client = new S3Client({
  region:      'ap-southeast-1',
  endpoint:    ENDPOINT,
  credentials: { accessKeyId: AWS_ACCESS_KEY || '', secretAccessKey: AWS_SECRET_KEY || '' },
  forcePathStyle: true,
  requestChecksumCalculation: 'WHEN_REQUIRED',
  responseChecksumValidation: 'WHEN_REQUIRED',
});

// Belt and braces: strip any checksum headers before the request is signed.
s3Client.middlewareStack.add(
  (next) => async (args) => {
    const headers = args?.request?.headers;
    if (headers) {
      Object.keys(headers).forEach((h) => {
        const lower = h.toLowerCase();
        if (lower.startsWith('x-amz-checksum-') || lower === 'x-amz-sdk-checksum-algorithm') {
          delete headers[h];
        }
      });
    }
    return next(args);
  },
  { step: 'build', name: 'stripChecksumHeaders', priority: 'low' }
);

const assertCredentials = () => {
  if (!AWS_ACCESS_KEY || !AWS_SECRET_KEY) {
    throw new Error(
      'Wasabi credentials are missing from this build (VITE_WASABI_ACCESS_KEY / VITE_WASABI_SECRET_KEY).'
    );
  }
};

/** Turns an SDK error into a readable one-line reason. */
export const describeWasabiError = (err) => {
  const status = err?.$metadata?.httpStatusCode;
  const name = err?.name || err?.Code || 'Error';
  if (!status && /network|failed to fetch/i.test(String(err?.message))) {
    return 'Network/CORS error: browser could not reach Wasabi (check bucket CORS rules).';
  }
  return `${name}${status ? ` (HTTP ${status})` : ''}: ${err?.message || 'Unknown error'}`;
};

// ── Stream → text (browser-native, no Node Buffer) ───────────────────────────

const streamToText = async (stream) => {
  if (stream instanceof ReadableStream) {
    const reader  = stream.getReader();
    const decoder = new TextDecoder();
    let result = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      result += decoder.decode(value, { stream: true });
    }
    result += decoder.decode();
    return result;
  }
  if (typeof stream.transformToString === 'function') {
    return stream.transformToString();
  }
  const chunks = [];
  for await (const chunk of stream) {
    chunks.push(typeof chunk === 'string' ? new TextEncoder().encode(chunk) : chunk);
  }
  const merged = new Uint8Array(chunks.reduce((acc, c) => acc + c.byteLength, 0));
  let offset = 0;
  for (const c of chunks) { merged.set(c, offset); offset += c.byteLength; }
  return new TextDecoder().decode(merged);
};

// ── Duplicate-detection ──────────────────────────────────────────────────────

const fileExists = async (key) => {
  try {
    await s3Client.send(new HeadObjectCommand({ Bucket: BUCKET_NAME, Key: key }));
    return true;
  } catch (err) {
    if (err?.name === 'NotFound' || err?.$metadata?.httpStatusCode === 404) return false;
    throw err;
  }
};

/**
 * Loops until it finds a free filename slot in Wasabi.
 *
 * Naming sequence for "ABC123.webm":
 *   1st upload  → ABC123.webm        (isDuplicate: false)
 *   2nd upload  → ABC123-d.webm      (isDuplicate: true, dupCount: 1)
 *   3rd upload  → ABC123-d2.webm     (isDuplicate: true, dupCount: 2)
 *   4th upload  → ABC123-d3.webm     (isDuplicate: true, dupCount: 3)
 *   … and so on
 */
const resolveFilename = async (filename) => {
  const dot = filename.lastIndexOf('.');
  const base = dot !== -1 ? filename.slice(0, dot) : filename;
  const ext  = dot !== -1 ? filename.slice(dot)    : '';

  // Check original first
  const originalKey = `${FOLDER_VIDEOS}${filename}`;
  if (!(await fileExists(originalKey))) {
    return { filename, key: originalKey, isDuplicate: false, dupCount: 0 };
  }

  // Loop: -d, -d2, -d3, …
  let count = 1;
  while (true) {
    const suffix     = count === 1 ? '-d' : `-d${count}`;
    const candidate  = `${base}${suffix}${ext}`;
    const candidateKey = `${FOLDER_VIDEOS}${candidate}`;
    if (!(await fileExists(candidateKey))) {
      return { filename: candidate, key: candidateKey, isDuplicate: true, dupCount: count };
    }
    count++;
  }
};

// ── CSV helpers ──────────────────────────────────────────────────────────────

const CSV_HEADER = 'Date & Time,AWB,URL\r\n';

const formatDatetime = (date) => {
  const p = (n) => String(n).padStart(2, '0');
  return (
    `${p(date.getDate())}-${p(date.getMonth() + 1)}-${date.getFullYear()} ` +
    `${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`
  );
};

const fetchExistingCsv = async (csvKey) => {
  try {
    const res = await s3Client.send(
      new GetObjectCommand({
        Bucket:               BUCKET_NAME,
        Key:                  csvKey,
        ResponseCacheControl: 'no-cache, no-store, must-revalidate',
      })
    );
    return await streamToText(res.Body);
  } catch (err) {
    const status = err?.$metadata?.httpStatusCode;
    if (err?.name === 'NoSuchKey' || err?.Code === 'NoSuchKey' || status === 404) return null;
    throw err;
  }
};

/**
 * Appends one row to ShipAssure/Files/<userId>.csv.
 *
 * @param {string} userId   - Supabase user ID
 * @param {string} awb      - Original AWB (shown in AWB column, never has -d suffix)
 * @param {string} urlKey   - Final stored filename WITHOUT extension (may include -d/-d2/etc.)
 * @param {string} datetime - Pre-formatted datetime string
 */
const appendCsvRow = async (userId, awb, urlKey, datetime) => {
  if (!userId || !awb) {
    console.warn('[wasabi] appendCsvRow skipped — missing userId or awb', { userId, awb });
    return;
  }

  const csvKey   = `${FOLDER_FILES}${userId}.csv`;
  // URL uses the FINAL stored key (with -d / -d2 / etc.) so the watch link resolves correctly
  const watchUrl = `${VIDEO_URL_PREFIX}${encodeURIComponent(urlKey)}`;
  const newRow   = `"${datetime}","${awb}","${watchUrl}"\r\n`;

  const existing   = await fetchExistingCsv(csvKey);
  const csvContent = existing ? existing + newRow : CSV_HEADER + newRow;

  await s3Client.send(new PutObjectCommand({
    Bucket:       BUCKET_NAME,
    Key:          csvKey,
    Body:         new TextEncoder().encode(csvContent),
    ContentType:  'text/csv',
    CacheControl: 'no-cache, no-store, must-revalidate',
    ACL:          'public-read',
  }));
};

// ── Diagnostics ──────────────────────────────────────────────────────────────

/**
 * Step-by-step connectivity test. Run in the browser console:
 *   await window.testWasabi()
 */
export const testWasabi = async () => {
  const results = [];
  const step = async (name, fn) => {
    try {
      await fn();
      results.push({ step: name, ok: true });
    } catch (err) {
      results.push({ step: name, ok: false, reason: describeWasabiError(err) });
    }
  };
  const testKey = `${FOLDER_FILES}_connection-test.txt`;

  await step('Credentials present', async () => assertCredentials());
  await step('HEAD (read access)', async () => {
    try {
      await s3Client.send(new HeadObjectCommand({ Bucket: BUCKET_NAME, Key: testKey }));
    } catch (e) {
      if (e?.$metadata?.httpStatusCode !== 404) throw e;
    }
  });
  await step('PUT (write access, no ACL)', () =>
    s3Client.send(new PutObjectCommand({
      Bucket: BUCKET_NAME, Key: testKey, Body: new TextEncoder().encode('ok'), ContentType: 'text/plain',
    })));
  await step('PUT with public-read ACL', () =>
    s3Client.send(new PutObjectCommand({
      Bucket: BUCKET_NAME, Key: testKey, Body: new TextEncoder().encode('ok'), ContentType: 'text/plain', ACL: 'public-read',
    })));
  await step('DELETE test file', () =>
    s3Client.send(new DeleteObjectCommand({ Bucket: BUCKET_NAME, Key: testKey })));

  console.table(results);
  return results;
};

if (typeof window !== 'undefined') window.testWasabi = testWasabi;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Uploads a video blob to Wasabi and appends a row to the user's CSV.
 *
 * @param {Blob}     blob        - The video blob
 * @param {string}   filename    - Desired filename, e.g. "ABC123.webm"
 * @param {Function} onProgress  - Progress callback (0-100)
 * @param {string}   userId      - Supabase user ID
 * @param {string}   originalAwb - The raw AWB string scanned by the user
 */
export const uploadToWasabi = async (blob, filename, onProgress, userId, originalAwb) => {
  assertCredentials();

  // 1. Find the first free filename slot (loops through -d, -d2, -d3, …)
  const { filename: finalFilename, key, isDuplicate, dupCount } = await resolveFilename(filename);

  // 2. Upload video
  const uint8Array = new Uint8Array(await blob.arrayBuffer());
  onProgress?.(10);
  await s3Client.send(new PutObjectCommand({
    Bucket:      BUCKET_NAME,
    Key:         key,
    Body:        uint8Array,
    ContentType: 'video/webm',
    ACL:         'public-read',
  }));
  onProgress?.(90);

  // 3. Build CSV values:
  //    AWB column  → always the clean original scanned AWB (no -d suffix)
  //    URL column  → built from the FINAL stored filename (includes -d/-d2/etc.)
  const awbForCsv = (originalAwb && originalAwb.trim())
    ? originalAwb.trim()
    : filename.replace(/\.webm$/i, '');

  // Strip .webm from the final filename to use as the URL key
  const urlKeyForCsv = finalFilename.replace(/\.webm$/i, '');

  const datetime = formatDatetime(new Date());

  try {
    await appendCsvRow(userId, awbForCsv, urlKeyForCsv, datetime);
  } catch (csvErr) {
    console.warn('[wasabi] CSV append failed (non-fatal):', csvErr);
  }

  onProgress?.(100);

  return {
    url:         `${ENDPOINT}/${BUCKET_NAME}/${key}`,
    filename:    finalFilename,
    isDuplicate,
    dupCount,
  };
};

/**
 * Downloads the per-user CSV from Wasabi.
 * Filename: ShipAssure_Videos_DD-MM-YYYY_HH-MM-SS.csv
 */
export const downloadUserCsv = async (userId) => {
  if (!userId) throw new Error('No user ID provided.');

  const csvKey = `${FOLDER_FILES}${userId}.csv`;

  const res = await s3Client.send(
    new GetObjectCommand({
      Bucket:               BUCKET_NAME,
      Key:                  csvKey,
      ResponseCacheControl: 'no-cache, no-store, must-revalidate',
    })
  );
  const text = await streamToText(res.Body);

  const dt   = formatDatetime(new Date()).replace(/:/g, '-').replace(' ', '_');
  const name = `ShipAssure_Videos_${dt}.csv`;

  const blob = new Blob([text], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};